import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Link, LinkProps, Location, useLocation, useNavigate } from 'react-router-dom';
import { ebeneVon, linkRegel, zurueckZiel } from '../../components/bereichsEbene';

// Einstellungen als Pop-up (AE-1 Nachtrag 3, SPEC AE-01): ob es offen ist und die echte Adresse. Der Hintergrund rendert
// mit seiner eigenen Adresse; wer die echte braucht (etwa AppLayout zum Einklappen der Leiste), liest sie hier.
export const EinstellungenKontext = createContext<{ offen: boolean; ort: Location | null }>({ offen: false, ort: null });
export type EinstellungsZustand = { hintergrund?: Location } | null;

// Plan 1a Welle 2 (SPEC-GEMEINSAM GB-06, Regel G-12): nichts geht still verloren. Ein Bereich meldet mit
// useUngespeichert(aktiv), dass er ungespeicherte Eingaben hat. Solange einer aktiv ist, fragt der Rahmen
// (Einstellungen.tsx) vor einem Wechsel in einen anderen Bereich und vor dem Schließen – mit den Wörtern aus VERWERFEN
// (administration/components.tsx): „Eingaben verwerfen?“, „Weiter bearbeiten“, „Verwerfen“. „Abbrechen“ in einem Formular
// bleibt die bewusste Wahl und fragt nicht; Browser-Zurück bleibt, wie es ist. Außerhalb von Pop-up und Bereichsseite
// (components/BereichsSeite.tsx, PF-07b) gibt es keine Wache.
export type UngespeichertWache = {
  /** Ein Bereich meldet sich an (aktiv) oder ab; kennung ist je Aufrufer eindeutig. */
  melden: (kennung: object, aktiv: boolean) => void;
  /** Vor einem Schritt, der Eingaben verwirft: gibt es welche, öffnet der Rahmen die Rückfrage, merkt sich den Schritt
   *  (weiter) und antwortet true – der Aufrufer führt ihn dann nicht selbst aus. Sonst false. */
  fragen: (weiter: () => void) => boolean;
};
export const UngespeichertKontext = createContext<UngespeichertWache | null>(null);

/** Die Wache ohne React: hält die Meldungen der Bereiche und ruft oeffnen(weiter), wenn gefragt werden muss. */
export function neueWache(oeffnen: (weiter: () => void) => void): UngespeichertWache {
  const meldungen = new Set<object>();
  return {
    melden: (kennung, aktiv) => { if (aktiv) meldungen.add(kennung); else meldungen.delete(kennung); },
    fragen: weiter => { if (meldungen.size === 0) return false; oeffnen(weiter); return true; },
  };
}

/** Ein Bereich meldet, dass er ungespeicherte Eingaben hat (nach dem Speichern und nach „Abbrechen“ wieder false). */
export function useUngespeichert(aktiv: boolean): void {
  const wache = useContext(UngespeichertKontext);
  const kennung = useRef<object>({});
  useEffect(() => {
    if (!wache) return;
    const eigene = kennung.current;
    wache.melden(eigene, aktiv);
    return () => wache.melden(eigene, false);
  }, [wache, aktiv]);
}

/** Für den Rahmen: die Wache, ob die Rückfrage offen ist, und ihre zwei Antworten. weiterKnopf („Weiter bearbeiten“) bekommt
 *  den Fokus, sobald die Frage erscheint; „Weiter bearbeiten“ gibt ihn dorthin zurück, wo er vorher lag (etwa ins Feld). */
export function useRueckfrage() {
  const weiterKnopf = useRef<HTMLButtonElement>(null);
  const vorher = useRef<HTMLElement | null>(null);
  const [gemerkt, setGemerkt] = useState<{ weiter: () => void } | null>(null);
  const wache = useMemo(() => neueWache(weiter => {
    const aktiv = document.activeElement;
    if (aktiv instanceof HTMLElement && !weiterKnopf.current?.parentElement?.contains(aktiv)) vorher.current = aktiv;
    setGemerkt({ weiter });
  }), []);
  useEffect(() => { if (gemerkt) weiterKnopf.current?.focus(); }, [gemerkt]);
  const weiterBearbeiten = useCallback(() => { setGemerkt(null); vorher.current?.focus(); }, []);
  const verwerfen = useCallback(() => { const weiter = gemerkt?.weiter; setGemerkt(null); weiter?.(); }, [gemerkt]);
  const zuruecksetzen = useCallback(() => setGemerkt(null), []);
  return { wache, frage: gemerkt !== null, weiterKnopf, weiterBearbeiten, verwerfen, zuruecksetzen };
}

/** Der Pfad einer Adresse ohne Abfrage, Anker und Schrägstrich am Ende. Ein Ziel mit demselben Pfad (nur ein anderer Anker)
 *  bleibt im Bereich; dort geht keine Eingabe verloren. */
export const pfadVon = (adresse: string) => adresse.split(/[?#]/)[0].replace(/\/+$/, '');

// Link auf einen Bereich der Einstellungen (SPEC AE-02, AE-11; Profil-Nachbau PF-07b, BAU-EINSTELLUNGEN.md B1.3 und
// BAU-FIRMA.md 1.8): Hintergrund, Verlaufseintrag und Rückfrage nennt linkRegel nach der Ebene des Links (App, Bereichsseite,
// Pop-up) und der des Ziels. Aus dem Programm heraus wird die aktuelle Ansicht der Hintergrund und das Öffnen legt einen
// Verlaufseintrag an; innerhalb einer Ebene bleibt der Hintergrund und der Eintrag wird ersetzt.
// GB-06: verlangt die Regel die Rückfrage, fragt die Wache der Ebene vor dem Wechsel, solange Eingaben ungespeichert sind.
// Profil-Nachbau F-13 (PF-724, WA-23): mitgeben legt weitere Angaben neben den Hintergrund in den state des Ziels (etwa die
// Abfrage der Mitgliederliste für den Rückweg der Mitgliedsseite); den Hintergrund ändert es nicht.
export function EinstLink({ to, onClick, mitgeben, ...rest }: Omit<LinkProps, 'to' | 'state' | 'replace'> & { to: string; mitgeben?: Record<string, unknown> }) {
  const location = useLocation();
  const navigate = useNavigate();
  const wache = useContext(UngespeichertKontext);
  const regel = linkRegel(location, pfadVon(to));
  const hintergrund: EinstellungsZustand = regel.hintergrund ? { hintergrund: regel.hintergrund } : null;
  const zustand = mitgeben ? { ...mitgeben, ...hintergrund } : hintergrund;
  const klick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    onClick?.(event);
    // Nur der gewöhnliche Klick wechselt an dieser Stelle (kein neuer Tab, kein neues Fenster).
    if (event.defaultPrevented || !regel.rueckfrage || !wache || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if ((rest.target && rest.target !== '_self') || pfadVon(to) === pfadVon(location.pathname)) return;
    if (wache.fragen(() => navigate(to, { state: zustand, replace: regel.replace }))) event.preventDefault();
  };
  return <Link to={to} state={zustand} replace={regel.replace} onClick={klick} {...rest} />;
}
// Link für Wege aus App und Menü in einen Bereich (Herkunft in state.hintergrund, B9.1); menue stellt UserMenu darauf um.
export const BereichLink = EinstLink;

// AE-02: im Programm geöffnet führt Schließen im Verlauf zurück, sonst ersetzt die Startansicht die Adresse. Dasselbe nutzt
// „Zur Recherche“ in „Mein Zugang“ (Nachbesserung B8); hintergrund nennt die Ansicht hinter dem Pop-up. Auf einer
// Bereichsseite (PF-07b) führt es wie „Zurück zum Chat“ zum App-Ort, sonst zur Startseite (zurueckZiel).
// GB-06: ein Bereich im Pop-up, der so schließt, fragt wie der Rahmen, solange Eingaben ungespeichert sind.
export function useSchliessen() {
  const location = useLocation();
  const navigate = useNavigate();
  const wache = useContext(UngespeichertKontext);
  const hintergrund = (location.state as EinstellungsZustand)?.hintergrund;
  const rueckweg = ebeneVon(location.pathname) === 'bereich' ? zurueckZiel(location) : null;
  const sofort = useCallback(() => {
    if (rueckweg !== null) navigate(rueckweg);
    else if (hintergrund && ((window.history.state as { idx?: number } | null)?.idx ?? 0) > 0) navigate(-1);
    else navigate('/', { replace: true });
  }, [rueckweg, hintergrund, navigate]);
  const schliessen = useCallback(() => { if (!wache?.fragen(sofort)) sofort(); }, [wache, sofort]);
  return { schliessen, hintergrund };
}
